import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { handleClick } from "lib/utils/navigator";
import { useFontSize } from "components/Resume/ResumePDF/FontSizeContext";

export const TemplateEightPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer",
      }}
    >
      {/* Wrap entire section to prevent orphaning */}
      <View wrap={false} style={{ ...styles.flexCol, gap: spacing["2"] }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children}
      </View>
    </div>
  );
};

//  orphan prevention 
export const TemplateEightEnhancedSection = ({
  themeColor,
  heading,
  children,
  textColor = "white", // Default white text for colored background
}: {
  themeColor?: string;
  heading?: string;
  children?: React.ReactNode;
  textColor?: string;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      {/* Wrap entire section to prevent orphaning */}
      <View wrap={false} style={{ ...styles.flexCol, gap: spacing["1"] }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: textColor,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
                <View
                  style={{
                    height: "1.25pt",
                    width: "100%",
                    backgroundColor: "white",
                    marginRight: spacing["3.5"],
                  }}
                  debug={DEBUG_RESUME_PDF_FLAG}
                />
              )}
            </View>
          )}
        {children}
      </View>
    </div>
  );
};

// section with subsections  in left column 
export const TemplateEightLeftColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child gets grouped with heading to prevent orphans
  const [firstChild, ...restChildren] = childrenArray;

  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        marginTop: spacing["3"],
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection container to prevent orphans */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        <View style={{ ...styles.flexCol, marginTop: spacing["1"] }}>
          {firstChild}
        </View>
      </View>
      
      {/* Rest of the subsections */}
      {restChildren.length > 0 && (
        <View style={{ ...styles.flexCol }}>
          {restChildren}
        </View>
      )}
    </div>
  );
};

//  section with subsections  in right column 
export const TemplateEightRightColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child  grouped with heading to prevent orphans
  const [firstChild, ...restChildren] = childrenArray;

  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        marginTop: spacing["3"],
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection container to prevent orphans */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "black",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: themeColor,
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        <View style={{ ...styles.flexCol, marginTop: spacing["1"] }}>
          {firstChild}
        </View>
      </View>
      
      {/* Rest of the subsections */}
      {restChildren.length > 0 && (
        <View style={{ ...styles.flexCol }}>
          {restChildren}
        </View>
      )}
    </div>
  );
};

export const ResumePDFDescriptionWithWhiteFont = ({
  items,
}: {
  items: string[];
}) => {
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                  lineHeight: 1.3,
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        // return <Text key={Math.random()}>{element.textContent}</Text>;
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor="white"
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["1"], // Reduced from spacing["2"] for better density
                      paddingRight: spacing["1"], // Reduced from spacing["2"] for better density
                      lineHeight: "1.3",
                    }}
                    bold={true}
                    themeColor="white"
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: "1.3", flexGrow: 1, flexBasis: 0 }}
                    themeColor="white"
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
      case "li":
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["1"], // spacing["2"] 
                paddingRight: spacing["1"], //   spacing["2"] 
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor="white"
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor="white"
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
      default:
        return null;
    }
  }
  return null;
};

export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

//  right column 
export const TemplateEightRightColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  return (
    <View 
      style={{ 
        ...styles.flexCol, 
        gap: spacing["1"], 
        marginTop: spacing["3"], 
      }}
      wrap={false} // Prevent section heading from being orphaned
      debug={DEBUG_RESUME_PDF_FLAG}
    >
      {heading && (
        <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
          <Text
            style={{
              ...dynamicStyles.heading,
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: "black", // Black text for white background
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "1.25pt",
                width: "100%",
                backgroundColor: themeColor, 
                marginRight: spacing["3.5"],
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}
      {children && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"], 
          }}
        >
          {children}
        </View>
      )}
    </View>
  );
};

//  text description component for right column (white background)
export const ResumePDFDescriptionBlackText = ({ items }: { items: string[] }) => {
  if (!items || items.length === 0) {
    return null;
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  const renderNodeWithBlackText = (node: Node): React.ReactNode => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      switch (element.nodeName.toLowerCase()) {
        case "p":
          return (
            <View>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1 }}
                themeColor="black"
              >
                {parseAndTransformHTML(element.innerHTML)}
                <br />
              </ResumePDFText>
            </View>
          );
          
        case "ol":
          return (
            <View style={{ ...styles.flexCol }}>
              {Array.from(element.childNodes).map((child, index) =>
                child.nodeName.toLowerCase() === "li" ? (
                  <View key={index} style={{ ...styles.flexRow }}>
                    <ResumePDFText
                      style={{
                        paddingLeft: spacing["1"],
                        paddingRight: spacing["1"],
                        lineHeight: 1.3,
                      }}
                      bold={true}
                      themeColor="black" 
                    >
                      {"•"}
                    </ResumePDFText>
                    <ResumePDFText
                      style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                      themeColor="black" 
                    >
                      {parseAndTransformHTML((child as Element).innerHTML)}
                    </ResumePDFText>
                  </View>
                ) : null
              )}
            </View>
          );
          
        case "li":
          return (
            <View style={{ ...styles.flexRow }}>
              <ResumePDFText
                style={{
                  paddingLeft: spacing["1"],
                  paddingRight: spacing["1"],
                  lineHeight: 1.3,
                }}
                bold={true}
                themeColor="black"
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{
                  lineHeight: 1.3,
                  flexGrow: 1,
                  flexBasis: 0,
                }}
                themeColor="black"
              >
                {parseAndTransformHTML(element.innerHTML)}
              </ResumePDFText>
            </View>
          );
          
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNodeWithBlackText(node)}</React.Fragment>
      ))}
    </View>
  );
};

//single-entry section for left column 
export const TemplateEightLeftColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} // Prevent orphan sections
        style={{
          ...styles.flexCol,
          gap: spacing["1"], 
          marginTop: spacing["3"], 
        }}
      >
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white", // White text for colored background
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white", 
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"], 
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};

// single-entry section for right column 
export const TemplateEightRightColumnSectionSingle = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} 
        style={{
          ...styles.flexCol,
          gap: spacing["1"], 
          marginTop: spacing["3"],
        }}
      >
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR, 
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: themeColor, 
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"], 
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};
